import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HelpCircle, Loader2, Send } from 'lucide-react';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { MarketingContactCollage } from '@/components/marketing/MarketingContactCollage';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/services/api';
import { ApiError } from '@/services/httpClient';
import { toast } from 'sonner';
import { PageSeo } from '@/components/seo/PageSeo';
import { contactSeo } from '@/lib/seo/marketingSeo';

type ContactType = 'general' | 'agency' | 'demo';

export default function ContactPage() {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const initialType = (searchParams.get('type') as ContactType) || 'general';
  const [type, setType] = useState<ContactType>(
    ['general', 'agency', 'demo'].includes(initialType) ? initialType : 'general',
  );
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const seo = contactSeo(language);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.public.submitContact({
        name,
        email,
        company: company || undefined,
        type,
        message,
        website,
        locale: language === 'it' ? 'it' : 'en',
      });
      setSent(true);
      toast.success(t('marketing.contact.success'));
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 503
          ? t('marketing.contact.errorUnavailable')
          : err instanceof ApiError && (err.status === 404 || err.status === 502)
            ? t('marketing.contact.errorUnavailable')
            : t('marketing.contact.errorGeneric');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-brand-surface-light text-foreground flex flex-col">
      <PageSeo title={seo.title} description={seo.description} path="/contact" />
      <MarketingNav />

      <main className="flex-1 container py-10 md:py-14">
        <header className="max-w-3xl mb-10 md:mb-12">
          <p className="text-sm font-medium text-brand-accent mb-2">{t('marketing.contact.badge')}</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-3">
            {t('marketing.contact.title')}
          </h1>
          <p className="text-muted-foreground leading-relaxed">{t('marketing.contact.subtitle')}</p>
        </header>

        <div className="grid lg:grid-cols-[1fr_minmax(320px,420px)] gap-10 lg:gap-14 items-start">
          <div className="hidden lg:block">
            <MarketingContactCollage />
          </div>

          <div className="space-y-8 w-full max-w-xl lg:max-w-none mx-auto lg:mx-0">
            <div className="lg:hidden">
              <MarketingContactCollage />
            </div>

            {sent ? (
              <div className="rounded-xl border border-border/60 bg-card p-8 text-center space-y-4">
                <p className="font-medium">{t('marketing.contact.successTitle')}</p>
                <p className="text-sm text-muted-foreground">{t('marketing.contact.successBody')}</p>
                <Button variant="outline" asChild>
                  <Link to="/">{t('marketing.contact.backHome')}</Link>
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-5 rounded-xl border border-border/60 bg-card p-6 md:p-8 shadow-sm"
              >
                <div className="space-y-2">
                  <Label htmlFor="contact-type">{t('marketing.contact.typeLabel')}</Label>
                  <Select value={type} onValueChange={(v) => setType(v as ContactType)}>
                    <SelectTrigger id="contact-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">{t('marketing.contact.type.general')}</SelectItem>
                      <SelectItem value="agency">{t('marketing.contact.type.agency')}</SelectItem>
                      <SelectItem value="demo">{t('marketing.contact.type.demo')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-name">{t('marketing.contact.name')}</Label>
                  <Input
                    id="contact-name"
                    required
                    maxLength={120}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">{t('marketing.contact.email')}</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    required
                    maxLength={254}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-company">{t('marketing.contact.company')}</Label>
                  <Input
                    id="contact-company"
                    maxLength={200}
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-message">{t('marketing.contact.message')}</Label>
                  <Textarea
                    id="contact-message"
                    required
                    minLength={10}
                    maxLength={5000}
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
                <div className="hidden" aria-hidden>
                  <Label htmlFor="contact-website">Website</Label>
                  <Input
                    id="contact-website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-brand-accent hover:bg-brand-accent/90"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {t('marketing.contact.submit')}
                </Button>
              </form>
            )}

            <Card className="border-border/60 bg-muted/20">
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center gap-2 text-brand-accent">
                  <HelpCircle className="h-5 w-5" aria-hidden />
                  <h2 className="font-semibold text-base">{t('marketing.faqPage.contactHint.title')}</h2>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('marketing.contact.faqTeaser')}
                </p>
                <Button variant="outline" asChild>
                  <Link to="/faq">{t('marketing.faqPage.homeCta')}</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
